import {AutoComplete} from '@/components/SendNotification/AutoComplete'
import {CustomImageSelector} from '@/components/SendNotification/CustomImageSelector'
import {LandingPageSelector} from '@/components/SendNotification/LandingPageSelector'
import {ImageUploadChangeValue} from '@/components/SingleImageUpload'
import {STEP_CONFIG} from '@/config/constants'
import {useCountries} from '@/hooks/useCountries'
import {NodeTypeEnum} from '@/types/NodeTypeEnum'
import {EmailCustomStepType} from '@/types/NotificationStep'
import {GetSendPushStepConfigResponse, LandingPageItem} from '@/types/StepConfigParams'
import {getAllDynamicTagsRtnUseCase} from '@/useCases/getAllTagsUseCase'
import {getStepConfigUseCase} from '@/useCases/getStepConfigUseCase'
import {MAX_MESSAGE_LENGTH} from '@/utils/consts'
import {useQuery} from '@tanstack/react-query'
import {useEffect} from 'react'
import {useIntl} from 'react-intl'

interface Props {
  emailStep: EmailCustomStepType
  setEmailStep: (emailStep: EmailCustomStepType) => void
}

export default function SendEmailParams({emailStep, setEmailStep}: Props): React.JSX.Element {
  const {formatMessage} = useIntl()
  const {country} = useCountries()

  const {
    isFetching: isFetchingEmailTags,
    data: dataEmailTags,
    error: errorEmailTags,
  } = useQuery({
    queryKey: ['dynamicTagsRtn', ['dynamicTagsRtn']],
    queryFn: ({signal}) => getAllDynamicTagsRtnUseCase({}, signal),
  })

  const {
    isFetching: isStepConfigEmailFetching,
    data: stepConfigEmailData,
    error: stepConfigEmailError,
  } = useQuery({
    // SendPush as Temp solution. It should be SendEmail when the backend is ready
    queryKey: [STEP_CONFIG.DATA_QUERY_KEY, NodeTypeEnum.sendPush, country],
    queryFn: ({signal}: {signal: AbortSignal}) =>
      getStepConfigUseCase(NodeTypeEnum.sendPush, signal) as GetSendPushStepConfigResponse,

    staleTime: 3 * 60 * 1000, // 3 minutes cache
  })

  const dynamicTagSuggestions = (dataEmailTags?.data.messageParameters ?? [])
    .filter(item => item.active)
    .map(item => item.name)
  const landingPageOptions = stepConfigEmailData?.landingPages ?? []
  const defaultLandingPage = landingPageOptions.find(option => option.isDefault)
  const imageOptions = stepConfigEmailData?.images ?? []

  const onSelectedPageChange = (selectedLandingPage: LandingPageItem) => {
    // const selectedPageItem = landingPageOptions.find(page => page.id === selectedLandingPage.id)
    setEmailStep({...emailStep, selectedPage: selectedLandingPage})
  }

  const onSelectedImageChange = (image: ImageUploadChangeValue) => {
    const imageUrl = typeof image === 'string' ? image : image.url ?? ''
    const selectedImageItem = imageOptions.find(img => img.url === imageUrl) ?? {id: imageUrl, url: imageUrl}
    setEmailStep({...emailStep, selectedImage: selectedImageItem})
  }

  useEffect(() => {
    if (!emailStep.selectedPage && defaultLandingPage) {
      setEmailStep({...emailStep, selectedPage: defaultLandingPage})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLandingPage])

  return (
    <div className="h-42 flex w-full max-w-xs flex-col gap-3" data-testid="send-email-params">
      {isFetchingEmailTags || (isStepConfigEmailFetching && <div>Data is Loading...</div>)}
      {errorEmailTags && <div>Error fetching dynamic tags: {errorEmailTags.message}</div>}
      {stepConfigEmailError && <div>Error fetching step configs: {stepConfigEmailError.message}</div>}

      {!isFetchingEmailTags && !errorEmailTags && (
        <>
          <AutoComplete
            label={formatMessage({id: 'NOTIFICATION_BUILDER.SUBJECT_LABEL'})}
            inputId="notification-email-subject"
            inputValue={emailStep.emailSubject}
            onChangeInput={(value: string) => setEmailStep({...emailStep, emailSubject: value})}
            max_message_length={MAX_MESSAGE_LENGTH}
            dynamicTagSuggestions={dynamicTagSuggestions}
            tagOffset={-100}
            required
          />
          <AutoComplete
            label={formatMessage({id: 'NOTIFICATION_BUILDER.MESSAGE_LABEL'})}
            inputId="notification-email-message"
            inputValue={emailStep.emailMessage}
            onChangeInput={(value: string) => setEmailStep({...emailStep, emailMessage: value})}
            max_message_length={MAX_MESSAGE_LENGTH}
            dynamicTagSuggestions={dynamicTagSuggestions}
            tagOffset={-100}
            required
          />
          <LandingPageSelector
            label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SELECT_LANDING_PAGE'})}
            options={landingPageOptions}
            value={emailStep.selectedPage || defaultLandingPage}
            onChange={onSelectedPageChange}
            disabled={false}
          />
          <CustomImageSelector
            label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SELECT_PUSH_IMAGE'})}
            value={emailStep.selectedImage?.url}
            onChange={onSelectedImageChange}
            disabled={false}
            required
          />
        </>
      )}
    </div>
  )
}
